import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit2, Trash2, Briefcase } from 'lucide-react'
import { masterDataApi } from '../../lib/api'
import type { BusinessTypePayload } from '../../lib/api'
import type { BusinessType } from '../../types'
import DataTable, { Column } from '../../components/shared/DataTable'
import LoadingState from '../../components/shared/LoadingState'
import EmptyState from '../../components/shared/EmptyState'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { tx } from '../../lib/i18n'

// ─── default form state ───────────────────────────────────────────────────────

const defaultForm: BusinessTypePayload = {
  name: '',
  description: '',
  isActive: true,
  sortOrder: 0,
}

// ─── component ────────────────────────────────────────────────────────────────

const BusinessTypesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [createModal, setCreateModal] = useState(false)
  const [editTarget, setEditTarget] = useState<BusinessType | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BusinessType | null>(null)
  const [form, setForm] = useState<BusinessTypePayload>(defaultForm)

  // ─── query ─────────────────────────────────────────────────────────────────

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['business-types'],
    queryFn: () => masterDataApi.listBusinessTypes().then((r) => r.data.data),
  })

  const businessTypes: BusinessType[] = data ?? []

  // ─── mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (payload: BusinessTypePayload) => masterDataApi.createBusinessType(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-types'] })
      showToast({ type: 'success', message: tx('Business type created.') })
      setCreateModal(false)
      setForm(defaultForm)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to create business type.' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<BusinessTypePayload> }) =>
      masterDataApi.updateBusinessType(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-types'] })
      showToast({ type: 'success', message: tx('Business type updated.') })
      setEditTarget(null)
      setForm(defaultForm)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to update business type.' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.deleteBusinessType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-types'] })
      showToast({ type: 'success', message: tx('Business type deleted.') })
      setDeleteTarget(null)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to delete business type.' })
    },
  })

  // ─── handlers ──────────────────────────────────────────────────────────────

  function openCreate() {
    setForm(defaultForm)
    setCreateModal(true)
  }

  function openEdit(bt: BusinessType) {
    setForm({
      name: bt.name,
      description: bt.description ?? '',
      isActive: bt.isActive,
      sortOrder: bt.sortOrder,
    })
    setEditTarget(bt)
  }

  function handleCreate() {
    if (!form.name.trim()) {
      showToast({ type: 'error', message: tx('Name is required.') })
      return
    }
    createMutation.mutate(form)
  }

  function handleUpdate() {
    if (!editTarget) return
    if (!form.name.trim()) {
      showToast({ type: 'error', message: tx('Name is required.') })
      return
    }
    updateMutation.mutate({ id: editTarget.id, payload: form })
  }

  // ─── columns ───────────────────────────────────────────────────────────────

  const columns: Column<BusinessType>[] = [
    {
      key: 'name',
      header: tx('Name'),
      sortable: true,
      render: (row) => (
        <span className="font-medium text-gray-900">{row.name}</span>
      ),
    },
    {
      key: 'description',
      header: tx('Description'),
      render: (row) => (
        <span className="text-gray-500 text-sm">{row.description ?? '—'}</span>
      ),
    },
    {
      key: 'isActive',
      header: tx('Status'),
      render: (row) =>
        row.isActive ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            {tx('Active')}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            {tx('Inactive')}
          </span>
        ),
    },
    {
      key: 'sortOrder',
      header: tx('Sort Order'),
      sortable: true,
      render: (row) => (
        <span className="text-gray-500 text-sm">{row.sortOrder}</span>
      ),
    },
    {
      key: 'actions',
      header: tx('Actions'),
      render: (row) => (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {can('master-data/business-types.update') && (
            <button
              onClick={() => openEdit(row)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title={tx('Edit')}
            >
              <Edit2 className="h-4 w-4" />
            </button>
            )}
            {can('master-data/business-types.delete') && (
            <button
              onClick={() => setDeleteTarget(row)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={tx('Delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
            )}
          </div>
      ),
    },
  ]

  // ─── loading / error states ─────────────────────────────────────────────────

  if (isLoading) return <LoadingState variant="spinner" />

  if (error) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{tx('Business Types')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">จัดการประเภทธุรกิจสำหรับร้านค้า</p>
          </div>
        </div>
        <EmptyState
          icon={Briefcase}
          title={tx('Failed to load business types')}
          description={tx('Please try again.')}
          action={{ label: tx('Retry'), onClick: () => refetch() }}
        />
      </div>
    )
  }

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Business Types')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">จัดการประเภทธุรกิจสำหรับร้านค้า</p>
        </div>
        {can('master-data/business-types.create') && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {tx('Add Business Type')}
          </button>
        )}
      </div>

      {/* Table */}
      {businessTypes.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title={tx('No business types yet')}
          description={tx('Add a business type to get started.')}
        />
      ) : (
        <DataTable
          columns={columns}
          data={businessTypes}
          emptyMessage={tx('No business types found.')}
        />
      )}

      {/* Create modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCreateModal(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Add Business Type')}</h3>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {tx('Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={tx('e.g. Hair Salon')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Description')}</label>
                <textarea
                  rows={3}
                  value={form.description ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value || null }))}
                  placeholder={tx('Optional description...')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Sort Order')}</label>
                <input
                  type="number"
                  value={form.sortOrder ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {/* isActive */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm font-medium text-gray-700">{tx('Active')}</span>
              </label>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button
                onClick={() => { setCreateModal(false); setForm(defaultForm) }}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {tx('Cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : tx('Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setEditTarget(null); setForm(defaultForm) }} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Edit Business Type')}</h3>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {tx('Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Description')}</label>
                <textarea
                  rows={3}
                  value={form.description ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value || null }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Sort Order')}</label>
                <input
                  type="number"
                  value={form.sortOrder ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {/* isActive */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                <span className="text-sm font-medium text-gray-700">{tx('Active')}</span>
              </label>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button
                onClick={() => { setEditTarget(null); setForm(defaultForm) }}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {tx('Cancel')}
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? tx('Saving...') : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title={tx('Delete Business Type')}
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone and may affect shops using this business type.`}
        confirmLabel={tx('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default BusinessTypesPage
